import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { showtoast } from '../../../util/toast';
import { calendarEventsSyncService } from '../../../services/calendarEventsSyncService';
import CalendarEventsModelMC from '../../models/CalendarEventsModelMC';
import { addSlotDuration } from './calendarTimezoneUtils';
import {
  normalizeEvent,
  buildShowEventPayload,
  buildPersistEventPayload
} from './calendarEventUtils';

const useCalendarEventsManager = ({
  slug: providedSlug,
  t,
  slotDuration = '00:30:00',
  defaultCreateDurationHours = 1
}) => {
  const { slug: routeSlug } = useParams();
  const slug = providedSlug || routeSlug || ENV.SLUG;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [calendarApi, setCalendarApi] = useState(null);
  const [showEvent, setShowEvent] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refreshCalendarEvents = useCallback(() => {
    calendarApi?.refetchEvents();
  }, [calendarApi]);

  const notifyCalendarChanged = useCallback((message = {}) => {
    calendarEventsSyncService.setSubject({
      changedAt: Date.now(),
      ...message
    });
  }, []);

  useEffect(() => {
    const subscription = calendarEventsSyncService.getSubject().subscribe(() => {
      refreshCalendarEvents();
    });

    return () => subscription.unsubscribe();
  }, [refreshCalendarEvents]);

  const loadEvents = useCallback(async (fetchInfo, successCallback, failureCallback) => {
    try {
      const events = await CalendarEventsModelMC.list({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr
      });
      const normalizedEvents = Array.isArray(events) ? events.map(normalizeEvent) : [];
      successCallback(normalizedEvents);
    } catch (error) {
      showtoast(t('messages.load_error'));
      failureCallback(error);
    }
  }, [t]);

  const calendarEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    loadEvents(fetchInfo, successCallback, failureCallback);
  }, [loadEvents]);

  const openCreateModal = useCallback(() => {
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);

    const end = new Date(start);
    end.setHours(end.getHours() + defaultCreateDurationHours);

    setSelectedEvent(null);
    setSelectedRange({ start, end });
    setShowEvent(null);
    setModalOpen(true);
  }, [defaultCreateDurationHours]);

  const closeCreateModal = useCallback(() => {
    calendarApi?.unselect();
    setModalOpen(false);
    setSelectedEvent(null);
    setSelectedRange(null);
  }, [calendarApi]);

  const handleRangeSelect = useCallback((selectionInfo) => {
    setShowEvent(null);
    setSelectedEvent(null);
    setSelectedRange({
      start: selectionInfo.start,
      end: selectionInfo.end
    });
    setModalOpen(true);
  }, []);

  const handleDateClick = useCallback((clickInfo) => {
    const start = clickInfo.date;
    const end = addSlotDuration(start, slotDuration);
    handleRangeSelect({ start, end });
  }, [handleRangeSelect, slotDuration]);

  const handleEventClick = useCallback((clickInfo) => {
    setShowEvent(buildShowEventPayload(clickInfo.event));
  }, []);

  const handleSave = useCallback(async (payload) => {
    try {
      if (selectedEvent?.id) {
        await CalendarEventsModelMC.update(selectedEvent.id, payload);
        showtoast(t('messages.updated'));
      } else {
        await CalendarEventsModelMC.create(payload);
        showtoast(t('messages.created'));
      }
      notifyCalendarChanged({ type: 'refetch' });
    } catch (error) {
      showtoast(t('messages.save_error'));
    }
  }, [notifyCalendarChanged, selectedEvent, t]);

  const handleDelete = useCallback(async (eventId) => {
    setPendingDeleteId(eventId);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    if (deleteLoading) return;
    setPendingDeleteId(null);
  }, [deleteLoading]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;

    setDeleteLoading(true);
    try {
      await CalendarEventsModelMC.destroy(pendingDeleteId);
      showtoast(t('messages.deleted'));
      notifyCalendarChanged({ type: 'refetch' });
      setPendingDeleteId(null);
    } catch (error) {
      showtoast(t('messages.delete_error'));
    } finally {
      setDeleteLoading(false);
    }
  }, [notifyCalendarChanged, pendingDeleteId, t]);

  const persistEventTimeChange = useCallback(async (eventInfo) => {
    try {
      await CalendarEventsModelMC.update(
        eventInfo.event.id,
        buildPersistEventPayload(eventInfo.event)
      );
      notifyCalendarChanged({ type: 'refetch' });
    } catch (error) {
      eventInfo.revert();
      showtoast(t('messages.save_error'));
    }
  }, [notifyCalendarChanged, t]);

  const handleEventDrop = useCallback(async (eventInfo) => {
    await persistEventTimeChange(eventInfo);
  }, [persistEventTimeChange]);

  const handleEventResize = useCallback(async (eventInfo) => {
    await persistEventTimeChange(eventInfo);
  }, [persistEventTimeChange]);

  const eventDidMount = useCallback((info) => {
    const lines = [info.event.title, info.timeText].filter(Boolean);
    info.el.title = lines.join('\n');
  }, []);

  const openEditFromShow = useCallback(() => {
    if (!showEvent) return;

    setSelectedRange(null);
    setSelectedEvent(showEvent);
    setShowEvent(null);
    setModalOpen(true);
  }, [showEvent]);

  const deleteFromShow = useCallback(async (eventIdFromModal) => {
    const eventId = eventIdFromModal || showEvent?.id;
    if (!eventId) return;
    setShowEvent(null);
    await handleDelete(eventId);
  }, [showEvent, handleDelete]);

  return {
    slug,
    modalOpen,
    closeCreateModal,
    selectedEvent,
    selectedRange,
    calendarApi,
    setCalendarApi,
    showEvent,
    setShowEvent,
    defaultStart: selectedRange?.start || null,
    defaultEnd: selectedRange?.end || null,
    loadEvents,
    calendarEvents,
    openCreateModal,
    handleRangeSelect,
    handleDateClick,
    handleEventClick,
    handleSave,
    handleDelete,
    isDeleteConfirmOpen: Boolean(pendingDeleteId),
    deleteLoading,
    confirmDelete,
    closeDeleteConfirm,
    persistEventTimeChange,
    handleEventDrop,
    handleEventResize,
    eventDidMount,
    openEditFromShow,
    deleteFromShow
  };
};

export default useCalendarEventsManager;
